import React, { useEffect, useState } from 'react';
import {
    Modal,
    Form,
    Button,
    Input,
    Select,
    message,
    Divider,
    Checkbox,
} from 'antd';
import useSWRImmutable from 'swr/immutable';
import { v4 as uuidv4 } from 'uuid';
import {
    GENERIC_ERROR_MESSAGE,
    PASSWORD_MISMATCH,
    USERNAME_TAKEN,
    validateMessages,
} from '../../constants/feedback';
import {
    USUARIOS_ENDPOINT,
    checkUsernameAvailability,
    createUsuario,
    getUsuarioById,
    updateUsuario,
} from '../../services/api/usuario';
import { ESTADO_ENDPOINT, getAllEstados } from '../../services/api/enumeracion';
import {
    PERFIL_BUSQUEDAS_KVP_ENDPOINT,
    getAllPerfilBusquedasAsKvpList,
} from '../../services/api/perfil-busqueda';
import {
    PERFIL_USUARIO_KVP_ENDPOINT,
    getAllPerfilUsuariosAsKvpList,
} from '../../services/api/perfil-usuario';

const { Option } = Select;

const itemLayout = {
    labelCol: { span: 24 },
};

const UsuarioItem = (props) => {
    const { isModalOpen, idUsuario, onSave, onCancel } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [isChagePasswordVisible, setIsChagePasswordVisible] = useState(false);
    const [isPasswordDisabled, setIsPasswordDisabled] = useState(true);
    const [form] = Form.useForm();

    const { data: perfilBusquedas } = useSWRImmutable(
        PERFIL_BUSQUEDAS_KVP_ENDPOINT,
        () => getAllPerfilBusquedasAsKvpList(),
        { revalidateOnMount: true }
    );

    const { data: perfilUsuarios } = useSWRImmutable(
        PERFIL_USUARIO_KVP_ENDPOINT,
        () => getAllPerfilUsuariosAsKvpList()
    );

    const { data: estados } = useSWRImmutable(ESTADO_ENDPOINT, () =>
        getAllEstados()
    );

    const { data: usuario, mutate } = useSWRImmutable(
        idUsuario > 0 ? [USUARIOS_ENDPOINT, idUsuario] : null,
        ([_, idUsuario]) => getUsuarioById(idUsuario),
        { revalidateOnMount: true }
    );

    useEffect(() => {
        if (idUsuario > 0 && usuario?.data) {
            let fields = Object.keys(usuario.data).map((key) => ({
                name: key,
                value: usuario.data[key],
            }));

            const password = uuidv4();

            fields.push(
                { name: 'password', value: password },
                { name: 'repetirPassword', value: password }
            );

            form.setFields(fields);

            setIsChagePasswordVisible(true);
            setIsPasswordDisabled(true);
        } else {
            setIsPasswordDisabled(false);
            setIsChagePasswordVisible(false);
        }
    }, [idUsuario, usuario, form]);

    const handleCancelClick = () => {
        form.resetFields();
        setIsPasswordDisabled(true);
        onCancel();
    };

    const handleFinishClick = async (values) => {
        try {
            setIsLoading(true);

            const params = { ...values, idUsuario };

            if (idUsuario > 0) {
                await updateUsuario(params);
                mutate();
            } else {
                await createUsuario(params);
            }

            form.resetFields();
            setIsPasswordDisabled(true);
            onSave();
        } catch (error) {
            message.error(GENERIC_ERROR_MESSAGE);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUsernameValidation = async (_, usuario) => {
        if (usuario && usuario.trim().length > 0) {
            const params = {
                usuario,
                idUsuario: idUsuario ?? 0,
            };

            try {
                const response = await checkUsernameAvailability(params);

                if (!response.data) {
                    return Promise.reject(USERNAME_TAKEN);
                }
            } catch (error) {
                message.error(GENERIC_ERROR_MESSAGE);
                return Promise.reject();
            }
        }

        return Promise.resolve(true);
    };

    const handleUpdatePasswordChange = (e) => {
        const checked = e.target.checked;
        const password = checked ? '' : uuidv4();

        form.setFieldsValue({
            password,
            repetirPassword: password,
        });

        setIsPasswordDisabled(!checked);
    };

    return (
        <Modal
            open={isModalOpen}
            title='Usuario'
            onCancel={handleCancelClick}
            destroyOnClose={true}
            footer={[
                <Button key='back' onClick={handleCancelClick}>
                    Cancelar
                </Button>,
                <Button
                    type='primary'
                    onClick={form.submit}
                    loading={isLoading}
                >
                    Guardar
                </Button>,
            ]}
            style={{ top: 20 }}
            width={800}
        >
            <Form
                form={form}
                onFinish={handleFinishClick}
                validateMessages={validateMessages}
                autoComplete='off'
            >
                <Divider className='mb-2'></Divider>

                <div className='row pl-2 pr-2'>
                    <div className='col-lg-6'>
                        <Form.Item
                            name='usuario'
                            label='Username'
                            {...itemLayout}
                            rules={[
                                {
                                    required: true,
                                    whitespace: true,
                                },
                                () => ({
                                    validator: handleUsernameValidation,
                                }),
                            ]}
                        >
                            <Input maxLength={50} allowClear />
                        </Form.Item>
                    </div>

                    <div className='col-lg-6'>
                        <Form.Item
                            name='correo'
                            label='Correo'
                            {...itemLayout}
                            rules={[
                                {
                                    required: true,
                                    whitespace: true,
                                },
                                {
                                    type: 'email',
                                },
                            ]}
                        >
                            <Input maxLength={50} allowClear />
                        </Form.Item>
                    </div>

                    <div className='col-lg-6'>
                        <Form.Item
                            name='nombre'
                            label='Nombre'
                            {...itemLayout}
                            className='mb-1'
                            rules={[
                                {
                                    required: true,
                                    whitespace: true,
                                },
                            ]}
                        >
                            <Input maxLength={50} allowClear />
                        </Form.Item>
                    </div>

                    <div className='col-lg-6'>
                        <Form.Item
                            name='telefono'
                            label='Teléfono'
                            {...itemLayout}
                            rules={[
                                {
                                    required: true,
                                    whitespace: true,
                                },
                            ]}
                            className='mb-0'
                        >
                            <Input maxLength={50} allowClear />
                        </Form.Item>
                    </div>

                    <Divider orientation='left' plain className='mb-0'>
                        <Form.Item
                            name='actualizarPassword'
                            valuePropName='checked'
                            className='mb-0'
                            hidden={!isChagePasswordVisible}
                        >
                            <Checkbox onChange={handleUpdatePasswordChange}>
                                Modificar contraseña
                            </Checkbox>
                        </Form.Item>
                    </Divider>

                    <div className='col-lg-6'>
                        <Form.Item
                            name='password'
                            label='Contraseña'
                            {...itemLayout}
                            rules={[{ required: true, whitespace: true }]}
                        >
                            <Input.Password
                                maxLength={100}
                                disabled={isPasswordDisabled}
                                allowClear
                            />
                        </Form.Item>
                    </div>

                    <div className='col-lg-6'>
                        <Form.Item
                            name='repetirPassword'
                            label='Repetir contraseña'
                            {...itemLayout}
                            rules={[
                                { required: true, whitespace: true },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (
                                            !value ||
                                            getFieldValue('password') === value
                                        ) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(
                                            new Error(PASSWORD_MISMATCH)
                                        );
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                maxLength={100}
                                disabled={isPasswordDisabled}
                                allowClear
                            />
                        </Form.Item>
                    </div>

                    <Divider orientation='right' plain></Divider>

                    <div className='col-lg-6'>
                        <Form.Item
                            name='idPerfilBusqueda'
                            label='Perfil búsqueda'
                            {...itemLayout}
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Select className='w-100' allowClear>
                                {perfilBusquedas?.data.map((perfilBusqueda) => (
                                    <Option
                                        value={perfilBusqueda.idPerfilBusqueda}
                                    >
                                        {perfilBusqueda.descripcion}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    <div className='col-lg-6'>
                        <Form.Item
                            name='idPerfilUsuario'
                            label='Perfil usuario'
                            {...itemLayout}
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Select className='w-100' allowClear>
                                {perfilUsuarios?.data.map((perfilUsuario) => (
                                    <Option
                                        value={perfilUsuario.idPerfilUsuario}
                                    >
                                        {perfilUsuario.perfil}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    <div className='col-lg-6'>
                        <Form.Item
                            name='idEstado'
                            label='Estado'
                            {...itemLayout}
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                        >
                            <Select className='w-100' allowClear>
                                {estados?.data.map((estado) => (
                                    <Option value={estado.id}>
                                        {estado.nombre}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>
                </div>
            </Form>
        </Modal>
    );
};

export default UsuarioItem;
